// FiltersBar — 1:1 port of legacy `src/ui/FiltersBar.jsx`.
// Declarative filter renderer: `search` → Input, `select` → Select,
// `chips` → toggle-buttons list, `range` → two numeric inputs.
import { useTranslation } from 'react-i18next';
import { Input } from './Input.js';
import { Select, type SelectOption } from './Select.js';

// WHY: FilterDef is generic over the consumer's filter shape F so that
// `key` is type-checked against the actual fields of F (typos are caught
// at the call-site). DOM events still hand us strings (or unknown for
// chips/range), so per-branch values flow through `as` narrowings at the
// use site — that residual is documented at the patch helper below.
export type FilterDef<F> =
  | { type: 'search'; key: Extract<keyof F, string>; label: string; placeholder?: string }
  | {
      type: 'select';
      key: Extract<keyof F, string>;
      label: string;
      options: SelectOption[];
      placeholder?: string;
    }
  | {
      type: 'chips';
      key: Extract<keyof F, string>;
      label: string;
      options: SelectOption[];
    }
  | { type: 'range'; key: Extract<keyof F, string>; label: string };

// WHY: generic over the consumer's filter shape so call-sites keep their
// strict struct types (e.g. `{ roleId: '' | RoleId; active: '' | 'true' |
// 'false' }`) without needing `as unknown as Record<string, unknown>`
// casts. Constraint is `extends object` rather than the wider
// `Record<string, unknown>`: strict struct types aren't assignable to
// `Record<string, unknown>` unless they declare an index signature, which
// would weaken consumer types unrelated to FiltersBar use.
export interface FiltersBarProps<F extends object> {
  filters: FilterDef<F>[];
  value: F;
  onChange: (next: F) => void;
}

export function FiltersBar<F extends object>({ filters, value, onChange }: FiltersBarProps<F>) {
  const { t } = useTranslation('common');
  // WHY: keys are type-checked against keyof F at the FilterDef level; the
  // single internal `as Record<string, unknown>` confines runtime indexing
  // to this component.
  const valueRec = value as Record<string, unknown>;
  // WHY: DOM events deliver strings (or unknown for chips/range) and the
  // consumer's F[K] may be a stricter type (e.g. branded `'' | RoleId`).
  // Bridging the form-string ↔ branded-domain boundary is the consumer's
  // responsibility (schema-parse the assembled filter at query time);
  // FiltersBar is a UI primitive and is not the right layer to validate.
  // The single `as F` is the residual from that boundary — keys are
  // type-safe (Extract<keyof F, string>), values are not.
  function patch<K extends Extract<keyof F, string>>(k: K, v: unknown) {
    onChange({ ...value, [k]: v } as F);
  }

  return (
    <div
      style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '10px 0' }}
    >
      {filters.map((f) => {
        if (f.type === 'search') {
          const v = typeof valueRec[f.key] === 'string' ? (valueRec[f.key] as string) : '';
          return (
            <div key={f.key} style={{ flex: '1 1 240px', maxWidth: 360 }}>
              <Input
                value={v}
                onChange={(nv) => patch(f.key, nv)}
                placeholder={f.placeholder || f.label}
                icon="search"
              />
            </div>
          );
        }
        if (f.type === 'select') {
          const v = typeof valueRec[f.key] === 'string' ? (valueRec[f.key] as string) : '';
          // Keep the visual chrome legacy-faithful (no visible label chip)
          // by hiding the label text via `clip`-style sr-only styles — the
          // `<label>` wrap still gives the child <select> an implicit
          // accessible name so `getByLabel(f.label)` works in tests and
          // screen-readers.
          return (
            <label key={f.key} style={{ minWidth: 160, display: 'block' }}>
              <span
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                {f.label}
              </span>
              <Select
                value={v}
                onChange={(nv) => patch(f.key, nv)}
                options={
                  f.options.some((option) => option.value === '')
                    ? f.options
                    : [
                        { value: '', label: t('filters.allPrefix', { label: f.label }) },
                        ...f.options,
                      ]
                }
              />
            </label>
          );
        }
        if (f.type === 'chips') {
          const raw = valueRec[f.key];
          const active: string[] = Array.isArray(raw) ? (raw as string[]) : [];
          return (
            <div key={f.key} style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {f.options.map((o) => {
                const isOn = active.includes(o.value);
                return (
                  <button
                    key={o.value}
                    onClick={() =>
                      patch(
                        f.key,
                        isOn ? active.filter((x) => x !== o.value) : [...active, o.value],
                      )
                    }
                    style={{
                      height: 28,
                      padding: '0 10px',
                      borderRadius: 'var(--r-full)',
                      background: isOn ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
                      color: isOn ? 'var(--accent-1-press)' : 'var(--fg-1)',
                      border: '1px solid ' + (isOn ? 'transparent' : 'var(--border-1)'),
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: isOn ? 500 : 400,
                    }}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          );
        }
        if (f.type === 'range') {
          const raw = valueRec[f.key];
          const v: { min?: string; max?: string } =
            raw && typeof raw === 'object' ? (raw as { min?: string; max?: string }) : {};
          return (
            <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{f.label}:</span>
              <input
                type="number"
                value={v.min ?? ''}
                onChange={(e) => patch(f.key, { ...v, min: e.target.value })}
                placeholder={t('filters.rangeMin')}
                style={{
                  width: 72,
                  height: 28,
                  padding: '0 6px',
                  borderRadius: 'var(--r-1)',
                  border: '1px solid var(--border-1)',
                  background: 'var(--bg-1)',
                  color: 'var(--fg-1)',
                  fontSize: 12,
                }}
              />
              <input
                type="number"
                value={v.max ?? ''}
                onChange={(e) => patch(f.key, { ...v, max: e.target.value })}
                placeholder={t('filters.rangeMax')}
                style={{
                  width: 72,
                  height: 28,
                  padding: '0 6px',
                  borderRadius: 'var(--r-1)',
                  border: '1px solid var(--border-1)',
                  background: 'var(--bg-1)',
                  color: 'var(--fg-1)',
                  fontSize: 12,
                }}
              />
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
